import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { OutboxEventDto } from "@/features/outbox/api/dto";
import type { OutboxService } from "@/features/outbox/api/endpoints";
import { useReplayOutboxEvent } from "@/features/outbox/api/use-replay-outbox-event";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: OutboxService;
  event: OutboxEventDto | null;
}

/**
 * Dialog de rejeu d'un `OutboxEvent` en DEAD_LETTER.
 * Motif obligatoire (traçabilité) — l'événement repart en PENDING.
 */
export function OutboxReplayDialog({ open, onOpenChange, service, event }: Props) {
  const { t } = useTypedTranslation();
  const [reason, setReason] = useState("");
  const replay = useReplayOutboxEvent();

  const handleConfirm = () => {
    if (!event || !reason.trim()) return;
    replay.mutate(
      { service, outboxEventId: event.id, reason: reason.trim() },
      {
        onSuccess: () => {
          notifSilence(t("admin.outbox.feedback.replayed"));
          setReason("");
          onOpenChange(false);
        },
        onError: (error) => notifSilenceError(error.message),
      }
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setReason("");
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("admin.outbox.replay.title")}</DialogTitle>
          <DialogDescription>
            {t("admin.outbox.replay.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 py-2">
          <Label htmlFor="replay-reason">{t("admin.outbox.replay.reason")}</Label>
          <Textarea
            id="replay-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("admin.outbox.replay.reasonPlaceholder")}
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!reason.trim() || replay.isPending}
          >
            {replay.isPending ? (
              <ButtonSpinner />
            ) : (
              t("admin.outbox.replay.action")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
